import { Product, Variant } from '@cabukaka/types'
import { Check, Heart, MapPin, Share2, ShoppingCart, Star, Store, Truck, Tag, MessageSquare } from 'lucide-react'
import { toast } from 'sonner'
import { useCart } from '../../../hooks/cart/useCart'
import { Link } from 'react-router-dom'
import { useFavorites } from '../../../hooks/favorites/useFavorites'
import { useState, useEffect } from 'react'
import ReviewModal from '../../shared/reviews/ReviewModal'
import { reviewService } from '@cabukaka/services'
import { useAuth, useToken } from '@cabukaka/hooks'
import { alertError } from '../../../helpers/alertError'

interface ProductDetailsProps {
    product: Product
}

export default function ProductDetails({ product }: ProductDetailsProps) {
    const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites()
    const { addToCart, isInCart } = useCart()
    const { user } = useAuth()
    const { token } = useToken()

    const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null)
    const [activeImage, setActiveImage] = useState<string>(product.mainImage)
    const [reviewModal, setReviewModal] = useState(false)
    const [stats, setStats] = useState<{ rating: number; reviews: number } | null>(null)

    const isFav = isFavorite(product.id)
    const isProductInCart = isInCart(
        product.priceMode === 'variant' && selectedVariant ? selectedVariant.id : product.id,
        selectedVariant?.id
    )

    // Reset complet si l'ID du produit change globalement
    useEffect(() => {
        setActiveImage(product.mainImage)
        setSelectedVariant(null)
    }, [product.id, product.mainImage])

    // Synchronisation automatique de l'image de la variante sélectionnée
    useEffect(() => {
        if (product.priceMode === 'variant') {
            setActiveImage(selectedVariant?.mainImage || product.mainImage)
        }
    }, [selectedVariant, product.priceMode, product.mainImage])

    // Charger les statistiques du produit en direct
    useEffect(() => {
        const fetchProductStats = async () => {
            try {
                const data = await reviewService.getProductStats(product.id)
                console.log(product.id)
                setStats(data)
            } catch (error) {
                console.error("Erreur lors de la récupération des stats de l'article:", error)
            }
        };
        if (product.id) fetchProductStats()
    }, [product.id])

    const handleFavoriteToggle = () => {
        if (isFav) {
            removeFromFavorites(product.id)
            toast.info(`${product.name} retiré des favoris`)
        } else {
            addToFavorites(product.id)
            toast.success(`${product.name} ajouté aux favoris`)
        }
    }

    const handleVariantSelect = (variant: Variant) => {
        setSelectedVariant(variant)
    }

    const handleAddToCart = () => {
        if (product.priceMode === 'variant' && !selectedVariant) {
            toast.error('Veuillez sélectionner une variante')
            return
        }
        
        if (product.priceMode === 'variant' && selectedVariant) {
            addToCart(product.id, 1, {
                id: selectedVariant.id,
                name: `${product.name} (${selectedVariant.name})`,
                value: selectedVariant.value,
                price: selectedVariant.price,
                mainImage: selectedVariant.mainImage
            })
        } else {
            addToCart(product.id, 1)
        }
        
        toast.success(`${product.name} ajouté au panier`)
    }

    const handleSubmitReview = async ({ comment, rating }: { comment: string, rating: number }) => {
        if (!user || !token) {
            alertError("Veuillez vous connecter pour donner votre avis");
            return;
        }
        try {
            await reviewService.baseService.create({
                rating, 
                comment, 
                shopId: product.shopId,
                productId: product.id,
                object: "product",
                client: {
                    name: user?.username,
                    phone: user.profile?.phone || "",
                    email: user.email
                }
            }, token);
            
            toast.success("Merci pour votre avis !");
            
            // Rafraîchissement dynamique des stats du produit
            const updatedStats = await reviewService.getProductStats(product.id);
            setStats(updatedStats);
            setReviewModal(false);
        } catch (error) {
            console.error(error);
            toast.error("Impossible d'enregistrer votre avis pour le moment.");
        }
    };

    const getCurrentPriceLabel = () => {
        if (product.priceMode === 'variant' && selectedVariant) {
            return selectedVariant.priceLabel
        }
        return product.priceLabel
    }

    const getCurrentStock = () => {
        if (product.priceMode === 'variant' && selectedVariant) {
            return selectedVariant.stock > 0
        }
        return product.inStock
    }

    const currentRating = stats ? stats.rating : (product.rating || 4.5)
    const currentReviewsCount = stats ? stats.reviews : (product.reviewsCount || 0)

    return (
        <div className="px-4 md:container md:mx-auto md:px-4 py-6 md:py-12 animate-in fade-in duration-300">
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12">
                
                {/* GALERIE IMAGES */}
                <div className="space-y-4">
                    <div className="relative aspect-square bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 group">
                        <img
                            src={activeImage}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        {getCurrentStock() ? (
                            <div className="absolute top-4 left-4 bg-emerald-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-sm z-10">
                                <Check className="w-3.5 h-3.5" />
                                En stock
                            </div>
                        ) : (
                            <div className="absolute top-4 left-4 bg-gray-950 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm z-10">
                                Rupture de stock
                            </div>
                        )}
                    </div>
                    
                    {/* Vignettes médias */}
                    {product.images && product.images.length > 1 && (
                        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                            <button 
                                onClick={() => setActiveImage(product.mainImage)}
                                className={`relative w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                                    activeImage === product.mainImage ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200 hover:border-gray-400'
                                }`}
                            >
                                <img src={product.mainImage} alt={product.name} className="w-full h-full object-cover" />
                            </button>
                            {product.images.filter(img => img !== product.mainImage).slice(0, 4).map((img, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setActiveImage(img)}
                                    className={`relative w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                                        activeImage === img ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200 hover:border-gray-400'
                                    }`}
                                >
                                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* INFOS PRODUIT */}
                <div className="space-y-6">
                    {/* Titre et note */}
                    <div>
                        <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3 tracking-tight leading-tight">
                            {product.name}
                        </h1>
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-1 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200/60">
                                <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                                <span className="font-bold text-amber-900 text-sm">{currentRating}</span>
                                <span className="text-amber-700/70 text-xs">({currentReviewsCount} avis)</span>
                            </div>
                            
                            <button 
                                onClick={() => setReviewModal(true)}
                                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-primary font-medium transition px-2 py-1 rounded-lg hover:bg-gray-50 border border-transparent hover:border-gray-100"
                            >
                                <MessageSquare className="w-3.5 h-3.5" />
                                Donner mon avis
                            </button>
                        </div>
                    </div>

                    {/* Zone d'affichage du Prix Principal Dynamique */}
                    <div className="bg-gradient-to-r from-gray-50 to-gray-50/30 rounded-2xl p-5 border border-gray-100 flex items-center justify-between">
                        <div className="space-y-1">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block">
                                {selectedVariant ? `Prix de l'option : ${selectedVariant.value}` : 'Prix du produit'}
                            </span>
                            <div className="flex items-baseline flex-wrap gap-3">
                                <span className="text-3xl md:text-4xl font-black text-primary tracking-tight">
                                    {getCurrentPriceLabel()}
                                </span>
                                {((selectedVariant && selectedVariant.originalPrice) || (!selectedVariant && product.originalPrice)) && (
                                    <span className="text-base md:text-lg text-gray-400 line-through font-medium">
                                        {selectedVariant?.originalPrice || product.originalPrice} FCFA
                                    </span>
                                )}
                            </div>
                        </div>
                        
                        {((selectedVariant && selectedVariant.originalPrice) || (!selectedVariant && product.originalPrice)) && (
                            <div className="bg-red-50 text-red-600 p-2.5 rounded-xl border border-red-100 flex items-center gap-1">
                                <Tag className="w-4 h-4 text-red-500" />
                                <span className="text-xs font-bold uppercase tracking-wider">Promo</span>
                            </div>
                        )}
                    </div>

                    {/* SÉLECTEUR DE VARIANTES */}
                    {product.priceMode === 'variant' && product.variants && product.variants.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="font-bold text-gray-900 text-sm md:text-base tracking-tight">
                                    Sélectionnez un modèle ou une édition disponibles :
                                </h3>
                            </div>
                            
                            <div className="space-y-3">
                                {product.variants.map((variant) => {
                                    const isSelected = selectedVariant?.id === variant.id;
                                    const isOutOfStock = variant.stock === 0;

                                    return (
                                        <button
                                            key={variant.id}
                                            disabled={isOutOfStock}
                                            onClick={() => handleVariantSelect(variant)}
                                            className={`w-full relative flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all duration-200 active:scale-[0.99] ${
                                                isSelected
                                                    ? 'border-primary bg-primary/[0.01] shadow-md ring-1 ring-primary'
                                                    : isOutOfStock 
                                                    ? 'border-gray-100 bg-gray-50/50 opacity-40 cursor-not-allowed'
                                                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                                            }`}
                                        >
                                            <div className="relative w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-50 border border-gray-100 shadow-inner">
                                                <img 
                                                    src={variant.mainImage || product.mainImage} 
                                                    alt={variant.value} 
                                                    className="w-full h-full object-cover"
                                                />
                                                {variant.originalPrice && !isOutOfStock && (
                                                    <div className="absolute bottom-0 inset-x-0 bg-red-500 text-[9px] text-white font-black text-center py-0.5 uppercase tracking-wide">
                                                        Soldé
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="flex-1 min-w-0 pr-4">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className={`font-bold text-base md:text-lg truncate ${isSelected ? 'text-primary' : 'text-gray-900'}`}>
                                                        {variant.value}
                                                    </p>
                                                </div>
                                                
                                                <div className="flex items-baseline gap-2 mt-1">
                                                    <span className={`font-black text-base ${isSelected ? 'text-primary' : 'text-gray-900'}`}>
                                                        {variant.priceLabel}
                                                    </span>
                                                    {variant.originalPrice && (
                                                        <span className="text-xs text-gray-400 line-through font-medium">
                                                            {variant.originalPrice} FCFA
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                <div className="mt-2">
                                                    {isOutOfStock ? (
                                                        <span className="inline-flex items-center text-[11px] font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-md">
                                                            Rupture de stock
                                                        </span>
                                                    ) : variant.stock <= 5 ? (
                                                        <span className="inline-flex items-center text-[11px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-md border border-orange-100 animate-pulse">
                                                            Plus que {variant.stock} disponibles !
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center text-[11px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                                                            Disponible immédiatement
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                                isSelected ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white'
                                            }`}>
                                                {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {selectedVariant && selectedVariant.description && (
                                <div className="p-4 bg-primary/[0.01] rounded-2xl border-2 border-dashed border-primary/20 text-xs md:text-sm text-gray-600 leading-relaxed animate-in slide-in-from-top-2 duration-200">
                                    <span className="font-bold text-primary block mb-1">Inclus avec cette option :</span>
                                    {selectedVariant.description}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Description générale */}
                    {product.description && (
                        <div className="space-y-2">
                            <h3 className="font-bold text-gray-900 text-sm md:text-base">À propos de cet article</h3>
                            <p className="text-gray-600 text-sm md:text-base leading-relaxed">{product.description}</p>
                        </div>
                    )}

                    {/* Boutique Vendeur */}
                    {product.shop && (
                        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-4 border border-gray-200/60 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0 border border-primary/10">
                                    <Store className="w-5 h-5" />
                                </div>
                                <div>
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block">Vendeur certifié</span>
                                    <p className="font-bold text-gray-900 text-sm md:text-base">{product.shop.name}</p>
                                    {product.shop.location?.city && (
                                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                                            <MapPin className="w-3 h-3 text-gray-400" />
                                            {product.shop.location.city}{product.shop.location.address && `, ${product.shop.location.address}`}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <Link to={`/boutiques/${product.shopId}`} className="text-xs md:text-sm font-bold text-primary hover:text-primary/80 whitespace-nowrap bg-white shadow-sm border border-gray-200 px-3 py-2 rounded-xl transition-colors">
                                Visiter
                            </Link>
                        </div>
                    )}

                    {/* Actions Cta */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                        <button
                            onClick={handleAddToCart}
                            disabled={!getCurrentStock() || isProductInCart}
                            className="flex-1 bg-primary hover:bg-primary/95 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg shadow-primary/20 flex items-center justify-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none active:scale-[0.99]"
                        >
                            {isProductInCart ? (
                                <>
                                    <Check className="w-5 h-5" />
                                    <span className="text-sm md:text-base">Déjà dans le panier</span>
                                </>
                            ) : (
                                <>
                                    <ShoppingCart className="w-5 h-5" />
                                    <span className="text-sm md:text-base">
                                        {product.priceMode === 'variant' && !selectedVariant 
                                            ? 'Sélectionnez un modèle ci-dessus' 
                                            : 'Ajouter au panier'}
                                    </span>
                                </>
                            )}
                        </button>
                        
                        <div className="flex gap-2">
                            <button
                                onClick={handleFavoriteToggle}
                                className="flex-1 sm:flex-none w-12 h-12 border-2 border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 text-gray-700 transition-all duration-200 flex items-center justify-center active:scale-95"
                                aria-label="Ajouter aux favoris"
                            >
                                <Heart className={`w-5 h-5 transition-transform duration-300 active:scale-125 ${isFav ? "fill-red-500 text-red-500" : ""}`} />
                            </button>
                            <button 
                                className="flex-1 sm:flex-none w-12 h-12 border-2 border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 text-gray-700 transition-all duration-200 flex items-center justify-center active:scale-95"
                                aria-label="Partager"
                            >
                                <Share2 className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Zone Livraison */}
                    <div className="bg-emerald-50/40 rounded-xl p-4 border border-emerald-100/70 flex items-start gap-3">
                        <div className="w-9 h-9 bg-emerald-500 text-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm shadow-emerald-500/20">
                            <Truck className="w-4 h-4" />
                        </div>
                        <div className="space-y-0.5">
                            <p className="font-bold text-gray-900 text-xs md:text-sm">Livraison express disponible</p>
                            <p className="text-xs text-gray-600 leading-relaxed">
                                Expédié rapidement à <span className="font-semibold text-gray-900">Douala</span>, <span className="font-semibold text-gray-900">Yaoundé</span> et sécurisé partout au Cameroun.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal d'évaluation */}
            <ReviewModal 
                title="Donnez votre avis sur cet article"
                isOpen={reviewModal}
                onClose={() => setReviewModal(false)}
                onSubmit={handleSubmitReview}
                icon={<Star className="w-5 h-5 text-amber-500 fill-amber-500" />}
            />
        </div>
    )
}